import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MapPin, Sun, Sunset, Moon, CalendarDays } from "lucide-react";

const LOC_NAMES = { "1": "Carleton Place", "2": "Kanata", "3": "Ottawa" };

export const todBucket = (hhmm) => {
  const h = parseInt(String(hhmm || "").slice(0, 2), 10);
  if (h < 12) return "morning";
  if (h < 16) return "afternoon";
  return "evening";
};

export const dateMatchesProvider = (d, view) => {
  if (view === "linda") return d.provider_hint === "linda";
  if (view === "karine") return d.provider_hint !== "linda";
  return true;
};

export function ProviderToggle({ value, onChange, isFr }) {
  const opts = [
    ["any", isFr ? "Première dispo" : "First available"],
    ["karine", "Karine, RN"],
    ["linda", isFr ? "Linda, IP · jeudis" : "Linda, NP · Thursdays"],
  ];
  return (
    <div className="mb-4" data-testid="provider-toggle">
      <p className="text-zinc-300 text-base mb-2">{isFr ? "Avec qui ?" : "Who would you like to see?"}</p>
      <div className="grid grid-cols-3 gap-2">
        {opts.map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => onChange(k)}
            className={`min-h-[52px] px-2 py-2 rounded-xl text-sm sm:text-base font-semibold leading-tight transition-colors ${
              value === k ? "bg-amber-500 text-zinc-950" : "bg-zinc-800 text-zinc-200 hover:bg-zinc-700"
            }`}
            data-testid={`provider-toggle-${k}`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function TimeOfDayFilter({ slots, value, onChange, isFr }) {
  const counts = { morning: 0, afternoon: 0, evening: 0 };
  slots.forEach((s) => { counts[todBucket(s.start_time)] += 1; });
  const present = Object.keys(counts).filter((k) => counts[k] > 0);
  if (present.length < 2) return null;
  const meta = {
    morning: [Sun, isFr ? "Matin" : "Morning"],
    afternoon: [Sunset, isFr ? "Après-midi" : "Afternoon"],
    evening: [Moon, isFr ? "Soir" : "Evening"],
  };
  return (
    <div className="flex flex-wrap gap-2 mb-4" data-testid="time-of-day-filter">
      <button
        type="button"
        onClick={() => onChange("all")}
        className={`min-h-[44px] px-4 rounded-full text-sm font-semibold ${value === "all" ? "bg-amber-500 text-zinc-950" : "bg-zinc-800 text-zinc-200 hover:bg-zinc-700"}`}
        data-testid="tod-all"
      >
        {isFr ? "Toute la journée" : "Any time"}
      </button>
      {present.map((k) => {
        const [Icon, label] = meta[k];
        return (
          <button
            key={k}
            type="button"
            onClick={() => onChange(k)}
            className={`min-h-[44px] px-4 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 ${value === k ? "bg-amber-500 text-zinc-950" : "bg-zinc-800 text-zinc-200 hover:bg-zinc-700"}`}
            data-testid={`tod-${k}`}
          >
            <Icon className="w-4 h-4" /> {label} <span className="opacity-70">({counts[k]})</span>
          </button>
        );
      })}
    </div>
  );
}

export function DayStrip({ dates, selectedDate, onPick, onShowAll, isFr }) {
  const ref = useRef(null);
  useEffect(() => {
    const strip = ref.current;
    const el = strip?.querySelector('[data-selected="1"]');
    if (strip && el) strip.scrollTo({ left: el.offsetLeft - (strip.clientWidth - el.clientWidth) / 2, behavior: "smooth" });
  }, [selectedDate?.date]);
  if (!dates || dates.length === 0) return null;
  const sel = selectedDate?.date;
  let shown = dates.slice(0, 21);
  if (sel && !shown.some((d) => d.date === sel)) {
    const i = dates.findIndex((d) => d.date === sel);
    shown = i >= 0 ? dates.slice(Math.max(0, i - 10), i + 11) : shown;
  }
  return (
    <div className="mb-4" data-testid="day-strip">
      <div className="flex items-center justify-between mb-2">
        <p className="text-zinc-300 text-base">{isFr ? "Changer de jour :" : "Change the day:"}</p>
        <button type="button" onClick={onShowAll} className="text-amber-400 text-sm font-semibold underline-offset-2 hover:underline inline-flex items-center gap-1 min-h-[44px] px-2" data-testid="day-strip-show-all">
          <CalendarDays className="w-4 h-4" /> {isFr ? "Tous les jours" : "See all days"}
        </button>
      </div>
      <div ref={ref} className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
        {shown.map((d) => {
          const isSel = sel === d.date;
          const [, m, day] = d.date.split("-");
          return (
            <button
              key={d.date}
              type="button"
              data-selected={isSel ? "1" : "0"}
              onClick={() => onPick(d)}
              className={`snap-start flex-shrink-0 min-w-[72px] min-h-[64px] rounded-xl px-3 py-2 text-center leading-tight transition-colors ${
                isSel ? "bg-amber-500 text-zinc-950" : "bg-zinc-800 text-zinc-200 hover:bg-zinc-700"
              }`}
              data-testid={`day-strip-${d.date}`}
            >
              <span className="block text-xs font-semibold uppercase">{(d.day_name || "").slice(0, 3)}</span>
              <span className="block text-lg font-bold">{parseInt(day, 10)}</span>
              <span className="block text-[11px] opacity-80">{new Date(2000, parseInt(m, 10) - 1, 1).toLocaleString(isFr ? "fr-CA" : "en-US", { month: "short" })}</span>
              {d.provider_hint === "linda" && <span className={`block text-[10px] font-semibold ${isSel ? "text-zinc-900" : "text-emerald-400"}`}>Linda</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const norm = (s) => String(s || "").toLowerCase().replace(/^#?[a-z]?\d+\s*[-–·:]?\s*/i, "").replace(/[^a-z0-9]+/g, "");
export const matchService = (list, svc) =>
  (svc.code && list.find((s) => s.code && String(s.code) === String(svc.code))) ||
  list.find((s) => norm(s.name) === norm(svc.name)) || null;

export function OtherClinicsCard({ currentId, onPick, isFr, locked, services = [] }) {
  const [offered, setOffered] = useState(null);
  const others = ["1", "2", "3"].filter((id) => id !== String(currentId));
  const sig = services.map((s) => s.variation_id).join(",");
  useEffect(() => {
    if (locked) return;
    let dead = false;
    Promise.all(others.map(async (id) => {
      try {
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/square/catalog-services/${id}`, { timeout: 15000 });
        const list = r.data?.services || [];
        const mapped = services.map((svc) => matchService(list, svc));
        return mapped.every(Boolean) ? { id, mapped } : null;
      } catch { return null; }
    })).then((rows) => { if (!dead) setOffered(rows.filter(Boolean)); });
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId, sig, locked]);
  if (locked || !offered || offered.length === 0) return null;
  return (
    <div className="rounded-xl border border-zinc-700 bg-zinc-800/60 p-4" data-testid="other-clinics-card">
      <p className="text-white text-base font-semibold mb-1">{isFr ? "Essayer une autre clinique ?" : "Try another clinic?"}</p>
      <p className="text-zinc-400 text-sm mb-3">{isFr ? "Ce traitement y est aussi offert — votre choix est conservé." : "This treatment is also offered there — your choice is kept."}</p>
      <div className={`grid gap-2 ${offered.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
        {offered.map(({ id, mapped }) => (
          <button
            key={id}
            type="button"
            onClick={() => onPick(id, mapped)}
            className="min-h-[56px] rounded-xl bg-zinc-900 border border-zinc-600 hover:border-amber-400 text-white text-base font-semibold inline-flex items-center justify-center gap-2"
            data-testid={`other-clinic-${id}`}
          >
            <MapPin className="w-4 h-4 text-amber-400" /> {LOC_NAMES[id]}
          </button>
        ))}
      </div>
    </div>
  );
}
